import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box, Button, CircularProgress, FormControl, Grid,
    IconButton, InputLabel, TextField, Typography,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LaunchIcon from '@material-ui/icons/Launch';
import _ from 'lodash';
import { QRCodeSVG } from 'qrcode.react';
import { baseDeeplink } from '../_constants/baseDeeplink';
import { mobilizeiStoresService } from '../_services/mobilizei/stores.service';
import { apiErrorMessage } from '../_helpers/apiError';

const useStyles = makeStyles(() => ({
    sectionTitle: { fontSize: 16, fontWeight: 'bold', margin: '16px 0 8px' },
    label: { fontWeight: 'bold', marginBottom: 0 },
    value: {
        margin: 0, overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', '-webkit-line-clamp': 2, '-webkit-box-orient': 'vertical',
    },
    image: { border: 'solid 1px #ececec', height: 'auto', maxWidth: '70%' },
    propertyContainer: { marginBottom: 12, '& a': { color: 'black', textDecoration: 'none' } },
    icon: { fontSize: 16 },
    fieldRow: { marginBottom: 16 },
    saved: { color: '#43a047', fontSize: 13, marginTop: 4 },
    saveError: { color: '#c62828', fontSize: 13, marginTop: 4 },
}));

function TextProperty({ data, label, name, classes }) {
    const copy = () => navigator.clipboard.writeText(data[name]);
    return (
        <div className={classes.propertyContainer}>
            <p className={classes.label}>{label}
                <IconButton size="small" onClick={copy}><FileCopyIcon className={classes.icon} /></IconButton>
            </p>
            <p className={classes.value}>{data[name] || '-'}</p>
        </div>
    );
}

function ImageProperty({ data, label, name, classes }) {
    return (
        <div className={classes.propertyContainer}>
            <a href={data[name]} target="_blank" rel="noreferrer">
                <p className={classes.label}>{label}
                    <IconButton size="small"><LaunchIcon className={classes.icon} /></IconButton>
                </p>
                {_.isEmpty(data[name]) ? '-' : <img className={classes.image} src={data[name]} alt={label} />}
            </a>
        </div>
    );
}

export default function StoreSetupStep({ store, setLocalStore, platformId, formData, isFinished }) {
    const classes = useStyles();

    const isNewApp = formData.isNewApp === true;
    const isAndroid = formData.mobilePlatform === 'android';
    const isIOS = formData.mobilePlatform === 'ios';
    const androidData = _.get(store, 'platforms.android', {});
    const iOSData = _.get(store, 'platforms.ios', {});

    // ascAppId inline capture (iOS new app only)
    const [ascAppId, setAscAppId] = useState(iOSData.ascAppId || '');
    const [savingAsc, setSavingAsc] = useState(false);
    const [ascSaved, setAscSaved] = useState(!!iOSData.ascAppId);
    const [ascError, setAscError] = useState(null);

    // For iOS new app: require ascAppId to be saved before allowing Next
    // For all other scenarios: always allow Next (instructions are informational)
    useEffect(() => {
        if (isIOS && isNewApp) {
            isFinished(ascSaved);
        } else {
            isFinished(true);
        }
    }, [ascSaved, isIOS, isNewApp]);

    const handleSaveAscAppId = () => {
        setSavingAsc(true);
        setAscError(null);
        mobilizeiStoresService
            .updateStoreById(platformId || store.platformId, store.id, {
                platforms: { ios: { ascAppId: ascAppId.trim() } },
            })
            .then(() => {
                setAscSaved(true);
                setLocalStore(prev => ({
                    ...prev,
                    platforms: {
                        ...prev.platforms,
                        ios: { ...(prev.platforms?.ios || {}), ascAppId: ascAppId.trim() },
                    },
                }));
            })
            .catch((e) => setAscError(apiErrorMessage(e, 'Erro ao salvar.')))
            .finally(() => setSavingAsc(false));
    };

    return (
        <Grid container direction="column">

            {/* ── Android ────────────────────────────────────────────── */}
            {isAndroid && (
                <>
                    <Box mb={1}>
                        <TextProperty data={androidData} label="Conta de desenvolvedor" name="developerAccountName" classes={classes} />
                    </Box>

                    <Typography className={classes.sectionTitle}>Ações necessárias</Typography>
                    <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                        {isNewApp ? (
                            <>
                                <Typography variant="body2">
                                    1 — Crie o aplicativo na Google Play Console com o package name abaixo.
                                </Typography>
                                <Typography variant="body2">
                                    2 — Adicione a service account da Mobilizei como usuário com permissão <strong>Release Manager</strong>:
                                </Typography>
                                <Typography variant="body2" style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, wordBreak: 'break-all' }}>
                                    {androidData.googlePlayServiceAccountName && store.firebaseProjectName
                                        ? `${androidData.googlePlayServiceAccountName}@${store.firebaseProjectName}.iam.gserviceaccount.com`
                                        : '(service account não disponível — prepare a loja primeiro)'}
                                    {androidData.googlePlayServiceAccountName && store.firebaseProjectName && (
                                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(`${androidData.googlePlayServiceAccountName}@${store.firebaseProjectName}.iam.gserviceaccount.com`)}>
                                            <FileCopyIcon className={classes.icon} />
                                        </IconButton>
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    3 — Complete a ficha da loja: política de privacidade, data safety form, content rating, descrição, screenshots, ícone e banner.
                                </Typography>
                                <Typography variant="body2">
                                    4 — Após o build completar em "Publicações (expo)", baixe o <strong>.aab</strong> e envie manualmente para produção no Google Play.
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="body2">
                                Atualize a página de detalhes do app na Google Play com as informações abaixo, se necessário.
                                O envio para revisão será feito automaticamente após o build.
                            </Typography>
                        )}
                    </Box>

                    <Typography className={classes.sectionTitle}>Dados do aplicativo</Typography>
                    <Grid container>
                        <Grid item xs={4}><TextProperty data={store} label="Título" name="title" classes={classes} /></Grid>
                        <Grid item xs={4}><TextProperty data={store} label="Descrição curta" name="shortDescription" classes={classes} /></Grid>
                        <Grid item xs={4}><TextProperty data={store} label="Vídeo promocional" name="promotionalVideo" classes={classes} /></Grid>
                        <Grid item xs={12}><TextProperty data={store} label="Descrição" name="description" classes={classes} /></Grid>
                        <Grid item xs={6}><ImageProperty data={store} label="Ícone" name="iconUrl" classes={classes} /></Grid>
                        <Grid item xs={6}><ImageProperty data={store} label="Banner da Play Store" name="featureGraphicUrl" classes={classes} /></Grid>
                    </Grid>

                    <Typography className={classes.sectionTitle}>Screenshots</Typography>
                    <Typography variant="body2" style={{ marginBottom: 8 }}>Utilize o visualizador para criar os screenshots:</Typography>
                    <QRCodeSVG value={`${baseDeeplink}&deep_link_value=${store.platformId}&deep_link_sub1=${store.id}`} size={100} />
                </>
            )}

            {/* ── iOS ────────────────────────────────────────────────── */}
            {isIOS && (
                <>
                    <Box mb={1}>
                        <TextProperty data={iOSData} label="Conta de desenvolvedor" name="teamName" classes={classes} />
                    </Box>

                    <Typography className={classes.sectionTitle}>Ações necessárias</Typography>
                    <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                        {isNewApp ? (
                            <>
                                <Typography variant="body2">
                                    1 — Crie o aplicativo na App Store Connect com o package name abaixo e copie o <strong>ID numérico do app</strong> (ascAppId) para o campo abaixo.
                                </Typography>
                                <Typography variant="body2">
                                    2 — Complete os metadados: App Privacy, Age Rating, Pricing, screenshots, descrição, palavras-chave e ícone.
                                </Typography>
                                <Typography variant="body2">
                                    3 — Após a publicação, selecione a compilação enviada pelo Expo na App Store Connect e envie para revisão em "Publicações (expo)".
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2">
                                    1 — Crie uma nova versão do aplicativo na App Store Connect.
                                </Typography>
                                <Typography variant="body2">
                                    2 — Atualize os dados abaixo na App Store Connect, se necessário.
                                </Typography>
                                <Typography variant="body2">
                                    3 — Após a publicação, selecione a compilação e envie para revisão em "Publicações (expo)".
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* ascAppId — obrigatório para app novo */}
                    {isNewApp && (
                        <Box mt={2} mb={2}>
                            <Typography className={classes.sectionTitle}>ID do app na App Store Connect (ascAppId)</Typography>
                            <Typography variant="body2" style={{ marginBottom: 8 }}>
                                Encontre o ID numérico do app na App Store Connect (ex.: 1234567890) e preencha abaixo. É necessário para o envio automático.
                            </Typography>
                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    value={ascAppId}
                                    onChange={(e) => { setAscAppId(e.target.value); setAscSaved(false); }}
                                    placeholder="ex.: 1234567890"
                                    disabled={ascSaved}
                                    style={{ width: 200 }}
                                />
                                {!ascSaved && (
                                    savingAsc
                                        ? <CircularProgress size={22} />
                                        : <Button variant="contained" color="primary" size="small" disabled={!ascAppId.trim()} onClick={handleSaveAscAppId}>Salvar</Button>
                                )}
                            </Box>
                            {ascSaved && <Typography className={classes.saved}>✅ Salvo. Clique em Próximo para continuar.</Typography>}
                            {ascError && <Typography className={classes.saveError}>{ascError}</Typography>}
                        </Box>
                    )}

                    {!isNewApp && (
                        <>
                            <Typography className={classes.sectionTitle}>Dados do aplicativo</Typography>
                            <Grid container>
                                <Grid item xs={6}><TextProperty data={store} label="Título" name="title" classes={classes} /></Grid>
                                <Grid item xs={6}><TextProperty data={store} label="Descrição curta" name="shortDescription" classes={classes} /></Grid>
                                <Grid item xs={12}><TextProperty data={store} label="Descrição" name="description" classes={classes} /></Grid>
                                <Grid item xs={12}><TextProperty data={store} label="Palavras-chave" name="keywords" classes={classes} /></Grid>
                                <Grid item xs={12}>
                                    <TextProperty
                                        data={{ whatsNew: 'Melhorias e ajustes para tornar sua experiência ainda melhor.' }}
                                        label="O que há de novo nesta versão"
                                        name="whatsNew"
                                        classes={classes}
                                    />
                                </Grid>
                            </Grid>

                            <Typography className={classes.sectionTitle}>Screenshots</Typography>
                            <Typography variant="body2" style={{ marginBottom: 8 }}>Utilize o visualizador para criar os screenshots:</Typography>
                            <QRCodeSVG value={`${baseDeeplink}&deep_link_value=${store.platformId}&deep_link_sub1=${store.id}`} size={100} />
                        </>
                    )}
                </>
            )}
        </Grid>
    );
}
